import React, { useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  WalletProvider as WalletReactProvider,
  useBrowserWallet,
  walletI18nResources,
  type WalletTranslator,
} from "@nullaone/wallet-react";
import i18n from "i18next";
import {
  BrowserWalletModal,
  TransactionConfirmationDialog,
  walletCoreConfig,
} from "@/features/wallet";
import { useToast } from "../hooks/useToast";
import { solanaService } from "../services/solana";

// Register the wallet-react i18n resources under the "wallet" namespace so
// the package's own keys resolve through the host i18next instance.
// Idempotent across HMR thanks to addResourceBundle's overwrite semantics.
let walletNamespaceRegistered = false;
function ensureWalletNamespace(): void {
  if (walletNamespaceRegistered) return;
  walletNamespaceRegistered = true;
  for (const [lang, resources] of Object.entries(walletI18nResources)) {
    i18n.addResourceBundle(lang, "wallet", resources, true, true);
  }
}

/**
 * Wallet Error Toast Component
 *
 * Reads `error` off the package's `useBrowserWallet` and surfaces it through
 * the branded toast. Must stay inside `<WalletReactProvider>` so the hook's
 * context resolves.
 */
function WalletErrorToast() {
  const { error } = useBrowserWallet();
  const { error: showError } = useToast();

  useEffect(() => {
    if (error) {
      showError("Wallet Error", {
        description: error,
      });
    }
  }, [error, showError]);

  return null;
}

interface WalletProviderProps {
  children: React.ReactNode;
}

/**
 * Wallet Provider Component
 *
 * Wraps the app with `<WalletReactProvider>` from `@nullaone/wallet-react`,
 * supplying the host-owned RPC client (JWT-injecting Kit transport), a toast
 * adapter that fans out to the branded toast, and the i18next translator
 * scoped to the `wallet` namespace.
 *
 * Renders the detrust_ai-branded `<BrowserWalletModal />`,
 * `<TransactionConfirmationDialog />`, and the error toast underneath. All
 * of them consume the package context/hooks, so they must live inside
 * `<WalletReactProvider>`.
 */
export function WalletProvider({ children }: WalletProviderProps) {
  ensureWalletNamespace();
  const { success, error } = useToast();
  const { t } = useTranslation("wallet");
  const rpc = solanaService.getRpc();

  // Widen the typed react-i18next `t` to the loose `(key, params?) => string`
  // shape wallet-react expects.
  const translator = useCallback<WalletTranslator>(
    (key, params) =>
      (t as unknown as (k: string, p?: Record<string, unknown>) => string)(
        key,
        params,
      ),
    [t],
  );

  // Stable adapter object — toast callbacks are stable, so memo keeps the
  // provider value referentially equal across renders.
  const toast = useMemo(
    () => ({
      success: (message: string) => success(message),
      error: (message: string) => error(message),
    }),
    [success, error],
  );

  return (
    <WalletReactProvider
      config={walletCoreConfig}
      rpc={rpc}
      toast={toast}
      t={translator}
    >
      <WalletErrorToast />
      <BrowserWalletModal />
      <TransactionConfirmationDialog />
      {children}
    </WalletReactProvider>
  );
}

export default WalletProvider;
